import type { HelpCitation, HelpCitationPassage } from "@esi/shared";
import { ArrowRight, LifeBuoy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Link } from "../router.tsx";
import {
  CHIP_CLASS,
  chipClass,
  type PassageRef,
  StateIcon,
  stateTextClass,
  usePassage,
  usePreviewPopover,
} from "./CitationChip.tsx";
import { type CitationState, stateText } from "./citation-state.ts";
import type { HelpTarget } from "./help-screens.ts";

/**
 * A product help citation (`H#`, spec `cited-answers` "Help citations"): an "ESI help" chip with
 * the page title whose preview shows the passage as cited (and "describes an earlier version of
 * ESI" after a release changed it). Like a knowledge chip, opening is a link inside the preview:
 * the page's screen when this viewer can open it here, resolved from the route name by
 * `helpTarget`; otherwise why there is no link ("ask an administrator", "open it from the
 * project").
 */
export function HelpCitationChip(props: {
  handle: string;
  help: HelpCitation | undefined;
  state: CitationState;
  passage: PassageRef | null;
  target: HelpTarget;
}) {
  const { handle, help, state, passage, target } = props;
  const { t } = useTranslation();
  const popover = usePreviewPopover();
  const title = help ? t("citations.help.chip", { title: help.title }) : t("citations.help.badge");
  const label = [title, help ? stateText(state, undefined) : t("citations.state.pending")].join(
    " · ",
  );
  const body = (
    <>
      <StateIcon state={state} />
      <LifeBuoy aria-hidden="true" className="size-2.5 shrink-0" strokeWidth={2} />
      <span aria-hidden="true" className="max-w-[12rem] truncate font-medium">
        {title}
      </span>
      <span className="sr-only">{label}</span>
    </>
  );
  return (
    <Popover {...popover.root}>
      <PopoverAnchor asChild>
        <button type="button" {...popover.trigger} className={cn(CHIP_CLASS, chipClass(state))}>
          {body}
        </button>
      </PopoverAnchor>
      <PopoverContent
        {...popover.content}
        side="top"
        align="start"
        className="w-[min(24rem,calc(100vw-2rem))] p-0"
        aria-label={t("citations.preview.label", { handle })}
      >
        <HelpPreview
          handle={handle}
          help={help}
          state={state}
          passage={passage}
          target={target}
          onOpen={popover.dismiss}
        />
      </PopoverContent>
    </Popover>
  );
}

function HelpPreview(props: {
  handle: string;
  help: HelpCitation | undefined;
  state: CitationState;
  passage: PassageRef | null;
  target: HelpTarget;
  onOpen: () => void;
}) {
  const { handle, help, state, passage, target, onOpen } = props;
  const reason = target.href === null ? target.reason : null;
  const { t } = useTranslation();
  const stored = usePassage(handle, passage, help !== undefined && passage !== null);
  const data: HelpCitationPassage | undefined =
    stored.data && "kind" in stored.data && stored.data.kind === "help" ? stored.data : undefined;
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-1 border-b border-line px-3 pt-3 pb-2.5">
        <p className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
          <LifeBuoy aria-hidden="true" className="size-3" strokeWidth={2} />
          {t("citations.help.badge")}
        </p>
        {help ? (
          <p className="text-sm leading-snug font-medium break-words text-fg">{help.title}</p>
        ) : null}
        <p
          className={cn(
            "inline-flex items-center gap-1 text-xs font-medium",
            stateTextClass(state),
          )}
        >
          <StateIcon state={state} />
          {stateText(state, undefined)}
        </p>
      </div>
      <div className="flex flex-col gap-2 px-3 py-2.5">
        {stored.isPending && stored.fetchStatus !== "idle" ? (
          <p className="text-xs text-fg-muted">{t("citations.preview.loading")}</p>
        ) : data?.excerpt ? (
          <blockquote
            // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrolling quote must be keyboard-scrollable (axe scrollable-region-focusable).
            tabIndex={0}
            className="max-h-48 overflow-y-auto border-l-2 border-line-strong pl-2.5 text-[13px] leading-relaxed break-words whitespace-pre-wrap text-fg-secondary"
          >
            {data.excerpt}
          </blockquote>
        ) : (
          <p className="text-xs text-fg-muted">{t("citations.help.noExcerpt")}</p>
        )}
        {data?.versionChanged ? (
          <p className="text-xs text-fg-muted">
            {data.pageGone ? t("citations.help.gone") : t("citations.help.earlier")}
          </p>
        ) : null}
        {reason === "adminOnly" || reason === "noProject" ? (
          <p className="text-xs text-fg-secondary">
            {reason === "adminOnly" ? t("citations.help.adminOnly") : t("citations.help.noProject")}
          </p>
        ) : null}
      </div>
      {help && target.href ? (
        <div className="flex items-center justify-end border-t border-line px-2 py-1.5">
          <Link
            href={target.href}
            onClick={onOpen}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-accent-text hover:bg-surface-hover"
          >
            {t("citations.help.open")}
            <ArrowRight aria-hidden="true" className="size-3" />
          </Link>
        </div>
      ) : null}
    </div>
  );
}
